import React, { useId, useMemo } from 'react';
import {
  PieChart as RechartsPie,
  Pie,
  Cell,
  Tooltip,
  Legend,
  type PieLabelRenderProps,
} from 'recharts';
import type { ColumnDef, PieMapping, VisualizationConfig } from '@deckspace/types';
import { getColorsForIntent, getThemeColors } from '../../theme/index.js';
import { PieChartTooltip } from '../../components/ChartTooltip.js';
import { prepPieSeries } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: PieMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

export function PieChart({ rows, columnDefs, mapping, config, width, height }: Props) {
  const uid = useId();

  const series = useMemo(
    () =>
      rows.length === 0 ? { labels: [], values: [] } : prepPieSeries(rows, columnDefs, mapping),
    [rows, columnDefs, mapping]
  );

  const chartData = useMemo(
    () =>
      series.labels.map((label, index) => ({
        name: label,
        value: series.values[index] ?? 0,
      })),
    [series.labels, series.values]
  );

  if (!series.labels.length) {
    return <div className="text-muted-foreground">No data available</div>;
  }

  const segmentCount = series.labels.length;
  const colors = getColorsForIntent(config.intent, segmentCount);
  const themeColors = getThemeColors();

  const formatValue = (value: number) => value.toLocaleString();

  return (
    <RechartsPie width={width ?? 400} height={height ?? 300}>
      <defs>
        {colors.map((color, index) => (
          <radialGradient
            key={`pie-gradient-${index}-${uid}`}
            id={`pie-gradient-${index}-${uid}`}
            cx="50%"
            cy="50%"
            r="50%"
          >
            <stop offset="0%" stopColor={color} stopOpacity={0.975} />
            <stop offset="100%" stopColor={color} stopOpacity={0.75} />
          </radialGradient>
        ))}
      </defs>
      <Pie
        data={chartData}
        cx="50%"
        cy="50%"
        labelLine={false}
        label={(props: PieLabelRenderProps) => (
          <text
            x={props.x as number}
            y={props.y as number}
            textAnchor={props.textAnchor as React.SVGAttributes<SVGTextElement>['textAnchor']}
            dominantBaseline={
              props.dominantBaseline as React.SVGAttributes<SVGTextElement>['dominantBaseline']
            }
            fill={themeColors.textSecondary}
            fontSize={12}
          >
            {`${props.name} (${((props.percent as number) * 100).toFixed(0)}%)`}
          </text>
        )}
        outerRadius="70%"
        fill="#8884d8"
        dataKey="value"
      >
        {chartData.map((_, index) => (
          <Cell
            key={`cell-${index}`}
            fill={`url(#pie-gradient-${index % colors.length}-${uid})`}
            stroke={colors[index % colors.length]}
            strokeWidth={2}
          />
        ))}
      </Pie>
      <Tooltip content={<PieChartTooltip formatter={formatValue} />} />
      <Legend wrapperStyle={{ color: themeColors.textPrimary }} />
    </RechartsPie>
  );
}
